import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Ticket, Plus, Trash2, Loader2, Copy, Check, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCoupons() {
  const { t, dir } = useI18n();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  // form state
  const [code, setCode] = useState("");
  const [kind, setKind] = useState("percent");
  const [value, setValue] = useState(20);
  const [duration, setDuration] = useState("once");
  const [durationMonths, setDurationMonths] = useState(3);
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [expiresDays, setExpiresDays] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/is-admin`).then(r => {
      setIsAdmin(r.data.is_admin);
    }).catch(() => setIsAdmin(false)).finally(() => setChecking(false));
  }, [user]);

  async function load() {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/coupons`);
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  async function submit(e) {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/coupons`, {
        code: code.trim().toUpperCase(),
        kind,
        value: Number(value),
        duration,
        duration_in_months: duration === "repeating" ? Number(durationMonths) : undefined,
        max_redemptions: maxRedemptions ? Number(maxRedemptions) : undefined,
        expires_days: expiresDays ? Number(expiresDays) : undefined,
      });
      toast.success(t("coupon_created") || "Coupon created");
      setCode(""); setValue(20); setMaxRedemptions(""); setExpiresDays("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  async function deactivate(id) {
    if (!window.confirm(t("confirm_deactivate") || "Deactivate this coupon?")) return;
    try {
      await axios.delete(`${API}/admin/coupons/${id}`);
      toast.success(t("coupon_deactivated") || "Deactivated");
      await load();
    } catch { toast.error("Failed"); }
  }

  function copy(text, id) {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  }

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  if (!user) {
    return (
      <div dir={dir} className="min-h-screen flex flex-col items-center justify-center text-zinc-100 px-6">
        <ShieldAlert className="w-10 h-10 text-amber-400 mb-3" />
        <p className="text-zinc-400 mb-4">{t("admin_login_required") || "Sign in as admin to continue."}</p>
        <Button onClick={login} className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black">
          {t("continue_google") || "Continue with Google"}
        </Button>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div dir={dir} className="min-h-screen flex flex-col items-center justify-center text-zinc-100 px-6 text-center">
        <ShieldAlert className="w-10 h-10 text-red-400 mb-3" />
        <h1 className="text-xl font-bold">{t("admin_only") || "Admin only"}</h1>
        <p className="text-zinc-500 mt-2 text-sm max-w-md">
          {t("admin_only_desc") || "Your account is not in the admin list. Ask the site owner to add your email to ADMIN_EMAILS."}
        </p>
        <Button onClick={() => navigate("/")} variant="outline" className="mt-6 rounded-full border-zinc-700">
          {t("back") || "Back"}
        </Button>
      </div>
    );
  }

  return (
    <div dir={dir} className="min-h-screen text-zinc-100 px-4 sm:px-8 py-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Ticket className="w-6 h-6 text-emerald-400" />
          <h1 className="text-2xl sm:text-3xl font-black">{t("admin_coupons") || "Discount codes"}</h1>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          <div className="sm:col-span-2">
            <Label className="text-xs text-zinc-400">{t("coupon_code_label") || "Promo code"}</Label>
            <Input
              data-testid="coupon-code"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="LAUNCH20"
              maxLength={40}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 uppercase"
              required
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">{t("coupon_kind") || "Type"}</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="coupon-kind" className="bg-zinc-900 border-zinc-800 text-zinc-100"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="percent">{t("coupon_percent") || "% Percentage"}</SelectItem>
                <SelectItem value="amount">{t("coupon_amount") || "$ Fixed amount"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">{kind === "percent" ? (t("coupon_percent_v") || "Percent off") : (t("coupon_amount_v") || "Amount off (USD)")}</Label>
            <Input
              data-testid="coupon-value"
              type="number" min="1" max={kind === "percent" ? 100 : 999}
              value={value}
              onChange={e => setValue(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              required
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">{t("coupon_duration") || "Duration"}</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="coupon-duration" className="bg-zinc-900 border-zinc-800 text-zinc-100"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="once">{t("dur_once") || "Once (first invoice)"}</SelectItem>
                <SelectItem value="repeating">{t("dur_repeat") || "Repeating (N months)"}</SelectItem>
                <SelectItem value="forever">{t("dur_forever") || "Forever"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {duration === "repeating" && (
            <div>
              <Label className="text-xs text-zinc-400">{t("months") || "Months"}</Label>
              <Input type="number" min="1" max="36" value={durationMonths} onChange={e => setDurationMonths(e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-zinc-100" />
            </div>
          )}
          <div>
            <Label className="text-xs text-zinc-400">{t("max_uses") || "Max uses (optional)"}</Label>
            <Input data-testid="coupon-max" type="number" min="1" value={maxRedemptions} onChange={e => setMaxRedemptions(e.target.value)}
              placeholder="unlimited" className="bg-zinc-900 border-zinc-800 text-zinc-100" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">{t("expires_days") || "Expires in days (optional)"}</Label>
            <Input data-testid="coupon-expires" type="number" min="1" value={expiresDays} onChange={e => setExpiresDays(e.target.value)}
              placeholder="never" className="bg-zinc-900 border-zinc-800 text-zinc-100" />
          </div>
          <div className="sm:col-span-2">
            <Button data-testid="coupon-submit" type="submit" disabled={busy} className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Plus className="w-4 h-4 mr-2" />{t("create_coupon") || "Create coupon"}</>)}
            </Button>
          </div>
        </form>

        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-800 text-sm text-zinc-400">
            {t("existing_coupons") || "Existing coupons"} · {items.length}
          </div>
          {loading ? (
            <div className="p-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-zinc-500 text-sm">{t("no_coupons") || "No coupons yet"}</div>
          ) : (
            <div className="divide-y divide-zinc-900">
              {items.map(it => (
                <div key={it.id} data-testid={`coupon-row-${it.code}`} className="px-5 py-4 flex items-center gap-3 flex-wrap">
                  <div className="font-mono text-emerald-400 font-bold tracking-wider" dir="ltr">{it.code}</div>
                  <div className="text-xs text-zinc-500" dir="ltr">
                    {it.kind === "percent" ? `${it.value}% off` : `$${it.value} off`}
                    {" · "}{it.duration}{it.duration === "repeating" ? ` (${it.duration_in_months}m)` : ""}
                    {it.times_redeemed != null && ` · used ${it.times_redeemed}${it.max_redemptions ? "/" + it.max_redemptions : ""}`}
                  </div>
                  <div className={"ms-auto text-[10px] uppercase tracking-wider " + (it.active === false ? "text-red-400" : "text-emerald-400")}>
                    {it.active === false ? (t("inactive") || "inactive") : (t("active") || "active")}
                  </div>
                  <button
                    onClick={() => copy(it.code, it.id)}
                    className="p-1.5 rounded-md hover:bg-zinc-900 text-zinc-400"
                    aria-label="copy"
                  >
                    {copiedId === it.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                  {it.active !== false && (
                    <button
                      data-testid={`coupon-delete-${it.code}`}
                      onClick={() => deactivate(it.id)}
                      className="p-1.5 rounded-md hover:bg-red-900/30 text-red-400"
                      aria-label="deactivate"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 text-xs text-zinc-500 text-center">
          {t("coupons_tip") || "Codes work automatically on the Stripe checkout page — users enter them in the promo code field."}
        </div>
      </div>
    </div>
  );
}
